import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Language } from '../data/translations';
const languages: {
  code: Language;
  label: string;
  name: string;
}[] = [{
  code: 'fr',
  label: 'FR',
  name: 'Français'
}, {
  code: 'en',
  label: 'EN',
  name: 'English'
}];
interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'segmented';
}
export function LanguageSwitcher({
  variant = 'dropdown'
}: LanguageSwitcherProps) {
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);
  if (variant === 'segmented') {
    return <div role="group" aria-label={t.nav.changeLanguage} className="grid grid-cols-2 gap-1 rounded-xl border border-grey-100 p-1" data-id="element-125" data-mp="1ynsgknkhl5p6">
        {languages.map(l => <button key={l.code} type="button" onClick={() => setLang(l.code)} aria-pressed={lang === l.code} className={`rounded-lg py-2 text-[15px] font-medium transition-colors ${lang === l.code ? 'bg-blue-reways-950-lock text-white' : 'text-blue-reways-950-lock hover:bg-blue-reways-50'}`} data-id="element-126" data-mp="2rbehtp68ry8v">
            {l.label}
          </button>)}
      </div>;
  }
  const current = languages.find(l => l.code === lang) ?? languages[0];
  return <div ref={ref} className="relative" data-id="element-127" data-mp="1cebvqyke4o8x">
      <button type="button" onClick={() => setIsOpen(v => !v)} aria-haspopup="listbox" aria-expanded={isOpen} aria-label={`${t.nav.changeLanguage} : ${current.name}`} className="flex items-center gap-1 rounded-lg px-2 py-2 text-[15px] text-blue-reways-950-lock transition-colors hover:bg-blue-reways-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-reways-500" data-id="element-128" data-mp="1ce24l7nm0b1n">
        {current.label}
        <ChevronDown className={`h-4 w-4 opacity-60 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-129" data-mp="fouh3u3fjrv3" />
      </button>

      <AnimatePresence data-id="element-130" data-mp="kp8r3holdbvr">
        {isOpen && <motion.ul role="listbox" aria-label={t.nav.changeLanguage} initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.15
      }} className="absolute right-0 top-full z-10 mt-2 w-40 overflow-hidden rounded-xl border border-grey-100 bg-white p-1 shadow-[0_12px_30px_rgba(8,8,82,0.12)]" data-id="element-131" data-mp="2a3djlwnz1vd4">
            {languages.map(l => <li key={l.code} role="option" aria-selected={lang === l.code} data-id="element-132" data-mp="2t6fdc7vza3bd">
                <button type="button" onClick={() => {
            setLang(l.code);
            setIsOpen(false);
          }} className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-blue-reways-950-lock transition-colors hover:bg-blue-reways-50" data-id="element-133" data-mp="37vv0zf07psmu">
                  <span data-id="element-134" data-mp="3ct3m9ognqcsb">
                    <span className="font-semibold" data-id="element-135" data-mp="1mi8ps053mwtf">{l.label}</span>
                    <span className="ml-2 text-grey-800-lock" data-id="element-136" data-mp="1mkl9ahqi9f8l">{l.name}</span>
                  </span>
                  {lang === l.code && <Check className="h-4 w-4" aria-hidden="true" data-id="element-137" data-mp="1jy2lfrckp4cc" />}
                </button>
              </li>)}
          </motion.ul>}
      </AnimatePresence>
    </div>;
}